import { z } from "zod";

/**
 * Persisted mutation provenance — the compact object every automation-relevant
 * write stores at a top-level CDC path on the changed entity
 * (docs/orchestration/01-event-catalog.md):
 *
 * - inserts persist `createdMutation` (visible in change-stream `fullDocument`);
 * - updates `$set` the whole `lastMutation` object, so change-stream routing
 *   can match `updateDescription.updatedFields.lastMutation.action`.
 *
 * Provenance describes who did what and when. It never carries request bodies,
 * PII, workflow context, or delivery state.
 */

export const MutationActorSchema = z.object({
  kind: z.enum(["user", "automation", "system"]),
  id: z.string().min(1),
});
export type MutationActor = z.infer<typeof MutationActorSchema>;

const MutationSourceSchema = z.enum(["api", "whatsapp", "workflow", "worker", "migration"]);
export type MutationSource = z.infer<typeof MutationSourceSchema>;

/** Actions persisted for provenance; only cataloged actions produce automation signals. */
const MutationActionSchema = z.enum([
  "task.created",
  "task.assigned",
  "task.updated",
  "task.completed",
  "task.canceled",
  "task.reopened",
  "case.updated",
  "case.step_changed",
  "case.requirements_blocked",
  "case.requirements_ready",
  "client.updated",
  "client.status_changed",
  "document.uploaded",
  "document.classified",
]);
export type MutationAction = z.infer<typeof MutationActionSchema>;

export const MutationProvenanceSchema = z.object({
  id: z.uuid(),
  action: MutationActionSchema,
  occurredAt: z.iso.datetime(),
  actor: MutationActorSchema,
  source: MutationSourceSchema,
  /** Triggering automation signal id; null for a human/system root mutation. */
  causationId: z.uuid().nullable(),
  automationDepth: z.number().int().min(0),
});
export type MutationProvenance = z.infer<typeof MutationProvenanceSchema>;

/** Causation propagated from the `Recobra-Causation-Id`/`-Automation-Depth` headers. */
export type MutationCausation = {
  causationId: string | null;
  automationDepth: number;
};

const randomUUID = (): string =>
  (globalThis as unknown as { crypto: { randomUUID(): string } }).crypto.randomUUID();

/**
 * Constructs one validated provenance object for the write that persists it.
 * This is the only supported constructor — producers never assemble the shape
 * by hand, and there is deliberately no handler middleware around it.
 */
export const createMutationProvenance = (input: {
  action: MutationAction;
  actor: MutationActor;
  source: MutationSource;
  causation?: MutationCausation;
}): MutationProvenance =>
  MutationProvenanceSchema.parse({
    id: randomUUID(),
    action: input.action,
    occurredAt: new Date().toISOString(),
    actor: input.actor,
    source: input.source,
    causationId: input.causation?.causationId ?? null,
    automationDepth: input.causation?.automationDepth ?? 0,
  });
